import { useMemo } from "react";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { currencyFormatter } from "@common/helpers/currency";
import type { View } from "@common/types/views/views";
import { ViewDensity } from "@common/types/views/views";
import type { ViewConfig } from "@lib/views";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableStickyFooter } from "@design-system/ViewTable/ViewTableStickyFooter";
import previewStyles from "../DatePickerPlayground.module.scss";
import tableStyles from "../TablesPlayground.module.scss";
import {
    BUSINESS_RECORDS,
    BusinessAmountCell,
    BusinessCategoryCell,
    BusinessDueDateCell,
    BusinessNameCell,
    BusinessOwnerCell,
    BusinessStatusCell,
    type BusinessRecordRow,
    SELECTION_HINT,
} from "./viewTablePlaygroundShared";

export type PinnedSort = "nameAsc" | "nameDesc";

export interface PinnedAndSortableState {
    view: View;
    sort: PinnedSort | null;
    rowSelection: RowSelectionState;
}

export interface PinnedAndSortableScenarioProps {
    density: ViewDensity;
    state: PinnedAndSortableState;
    onViewChange: (view: View) => void;
    onSortChange: (sort: PinnedSort) => void;
    onSortRemove: () => void;
    onRowSelectionChange: OnChangeFn<RowSelectionState>;
}

export const createPinnedAndSortableState = (): PinnedAndSortableState => ({
    view: {
        columns: [
            { id: "name", options: { pin: "left" } },
            { id: "owner" },
            { id: "category" },
            { id: "dueDate" },
            { id: "status" },
            { id: "amount", options: { pin: "right" } },
        ],
        filters: {},
        options: {
            selection: true,
            bulkActions: true,
            density: ViewDensity.MD,
        },
    },
    sort: null,
    rowSelection: {},
});

type PinnedColumn =
    | "name"
    | "owner"
    | "category"
    | "dueDate"
    | "status"
    | "amount";

const pinnedConfig: ViewConfig<BusinessRecordRow, PinnedColumn, PinnedSort> = {
    columns: {
        name: {
            accessorKey: "name",
            name: "Name",
            header: "Name",
            cell: BusinessNameCell,
            minSize: 240,
            sort: {
                asc: "nameAsc",
                desc: "nameDesc",
                defaultOrder: "asc",
            },
        },
        owner: {
            accessorKey: "owner",
            name: "Owner",
            header: "Owner",
            cell: BusinessOwnerCell,
            minSize: 180,
        },
        category: {
            accessorKey: "category",
            name: "Category",
            header: "Category",
            cell: BusinessCategoryCell,
            minSize: 180,
        },
        dueDate: {
            accessorKey: "dueDate",
            name: "Due date",
            header: "Due date",
            cell: BusinessDueDateCell,
            minSize: 180,
        },
        status: {
            accessorKey: "status",
            name: "Status",
            header: "Status",
            cell: BusinessStatusCell,
            minSize: 180,
        },
        amount: {
            accessorKey: "amount",
            name: "Amount",
            header: "Amount",
            cell: BusinessAmountCell,
            align: "right",
            minSize: 160,
        },
    },
};

const SORT_LABELS: Record<PinnedSort, string> = {
    nameAsc: "Name ascending",
    nameDesc: "Name descending",
};

export const PinnedAndSortableScenario = ({
    density,
    state,
    onViewChange,
    onSortChange,
    onSortRemove,
    onRowSelectionChange,
}: PinnedAndSortableScenarioProps) => {
    const displayedRows = useMemo(() => {
        if (state.sort === null) {
            return BUSINESS_RECORDS;
        }
        const direction = state.sort === "nameAsc" ? 1 : -1;
        return [...BUSINESS_RECORDS].sort(
            (left, right) => direction * left.name.localeCompare(right.name),
        );
    }, [state.sort]);

    const selectedLabels = displayedRows
        .filter((row) => state.rowSelection[row.id])
        .map((row) => row.name);
    const columnWidths = state.view.columns
        .map((column) => `${column.id}: ${column.options?.size ?? "Default"}`)
        .join(", ");
    const view = useMemo<View>(
        () => ({
            ...state.view,
            options: {
                ...state.view.options,
                density,
            },
        }),
        [state.view, density],
    );

    return (
        <>
            <span>{SELECTION_HINT}</span>
            <div className={tableStyles.tableFrame}>
                <ViewTable
                    data={displayedRows}
                    view={view}
                    config={pinnedConfig}
                    sort={state.sort ?? undefined}
                    onSortChange={onSortChange}
                    onSortRemove={onSortRemove}
                    rowSelection={state.rowSelection}
                    onRowSelectionChange={onRowSelectionChange}
                    onViewChange={onViewChange}
                    enableColumnResizing
                    outerBorder
                    style={{ height: "360px" }}
                    footer={
                        <ViewTableStickyFooter>
                            <span>{displayedRows.length} records</span>
                            <span>
                                Total{" "}
                                {currencyFormatter.format(
                                    displayedRows.reduce(
                                        (sum, row) => sum + row.amount,
                                        0,
                                    ),
                                )}
                            </span>
                        </ViewTableStickyFooter>
                    }
                />
            </div>
            <div className={previewStyles.output}>
                <span>State</span>
                <pre role="status" aria-live="polite">
                    {`Sort: ${
                        state.sort === null ? "None" : SORT_LABELS[state.sort]
                    }\nSelected: ${selectedLabels.length} of ${BUSINESS_RECORDS.length}: ${
                        selectedLabels.length === 0
                            ? "None"
                            : selectedLabels.join(", ")
                    }\nColumn widths: ${columnWidths}`}
                </pre>
            </div>
        </>
    );
};
